import { useRef, useState } from "react";
import { css } from "@emotion/react";
import Close from "@mui/icons-material/Close";
import ModeEditOutlined from "@mui/icons-material/ModeEditOutlined";
import {
  Box,
  CardMedia,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Element } from "react-scroll";

import type { UploadedFile } from "@thalamos/frontend";
import { emptyImage } from "@thalamos/aspire-common";

import { FormLabel } from "../../design-system/FormLabel.js";
import { FormTitle } from "../../design-system/FormTitle/FormTitle.js";
import { HelperText } from "../../design-system/HelperText/HelperText.js";
import type { IconType } from "../../types/subText.js";
import { HorizontalLine } from "../HorizontalLine.js";
import {
  SignatureDialog,
  SignatureTabsOptions,
} from "../SignatureDialog/SignatureDialog.js";
import { SignatureTabs } from "./SignatureTabs.js";
import { UploadedFileSection } from "./UploadedFileSection.js";
import type { SignatureCanvasInstance } from "~/lib/compat/reactSignatureCanvas.js";

export type SignatureProps = {
  name: string;
  accountSignature?: string;
  formSignature?: string;
  label?: string;
  checkboxLabel?: string;
  useCheckbox?: boolean;
  saveSignature: (signature: string | undefined) => void;
  confirmButtonLabel?: string;
  clearButtonLabel?: string;
  useExistingSignature?: boolean;
  changeUseExistingSignature?: (useExistingSignature: boolean) => void;
  errorMessage?: string;
  subtext?: string;
  subtextIcon?: IconType;
  userDefaultSignature?: string;
  hideSavedSignature?: boolean;
  showSaveDefaultSignatureOption?: boolean;
  allowUseOfDefaultSignature?: boolean;
};

const defaultProps: Partial<SignatureProps> = {
  useCheckbox: false,
  useExistingSignature: false,
  confirmButtonLabel: "I AM HAPPY WITH MY SIGNATURE",
  clearButtonLabel: "CLEAR",
  allowUseOfDefaultSignature: true,
};

export const Signature = (props: SignatureProps) => {
  const {
    formSignature,
    accountSignature,
    label,
    useExistingSignature,
    useCheckbox,
    name,
    saveSignature,
    checkboxLabel,
    confirmButtonLabel,
    clearButtonLabel,
    changeUseExistingSignature,
    errorMessage,
    subtext,
    userDefaultSignature,
    hideSavedSignature,
    showSaveDefaultSignatureOption,
    allowUseOfDefaultSignature,
  } = { ...defaultProps, ...props };

  const theme = useTheme();
  const [useDialogSignature, changeUseDialogSignature] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(SignatureTabsOptions.DRAW);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);

  const signPad = useRef<SignatureCanvasInstance>({} as any);

  const handleClear = () => {
    setFileError(null);
    signPad?.current?.clear();
    setUploadedFile(null);
  };

  const { t } = useTranslation();

  const getControl = () => (
    <Checkbox
      checked={useExistingSignature}
      onClick={() => {
        changeUseDialogSignature(false);
      }}
      onChange={(e) => {
        if (!e.target.checked && !useDialogSignature) {
          saveSignature(emptyImage);
        }

        changeUseExistingSignature &&
          changeUseExistingSignature(!useExistingSignature);

        !useExistingSignature &&
          accountSignature &&
          saveSignature(accountSignature);
      }}
      name={`${name}-useExistingSignature`}
    />
  );

  const onHappyWithSignature = async (signature: string | undefined) => {
    saveSignature(signature);
    changeUseDialogSignature(true);
    changeUseExistingSignature && changeUseExistingSignature(false);
  };

  const imageSignatureCss = css`
    background-color: ${theme.palette.background.default};
    border-radius: 2px 2px 0 0;
    height: ${theme.spacing(15.5)};
    object-fit: contain;
    object-position: center;
  `;

  return (
    <Element name={name}>
      <FormControl
        css={css`
          width: 100%;
        `}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <FormTitle
            useReducedTopPadding
            hasTitleBottomMargin={false}
            subtitleText={t("pages.common.signature.label")}
          />
          {formSignature && (
            <IconButton>
              <ModeEditOutlined
                sx={{ color: "primary.hint" }}
                onClick={() => setIsOpen(true)}
              />
            </IconButton>
          )}
        </Box>
        {label && <FormLabel label={label} error={false} />}
        {useCheckbox && (
          <FormControlLabel
            control={getControl()}
            label={checkboxLabel}
            css={css`
              margin-bottom: ${theme.spacing(2)};
            `}
          />
        )}
        {isOpen && (
          <SignatureDialog
            onClose={() => setIsOpen(false)}
            onHappyWithSignature={onHappyWithSignature}
            confirmButtonLabel={confirmButtonLabel!}
            clearButtonLabel={clearButtonLabel!}
            userDefaultSignature={userDefaultSignature}
            hideSavedSignature={hideSavedSignature}
            showSaveDefaultSignatureOption={showSaveDefaultSignatureOption}
            allowUseOfDefaultSignature={allowUseOfDefaultSignature}
          />
        )}

        {formSignature && (
          <>
            <Box
              css={css`
                padding: ${theme.spacing(2, 3.25, 2, 3.25)};
                border: 1px dashed ${theme.palette.common.lightGray};
                border-radius: 6px;
              `}
            >
              <Box sx={{ mx: 4 }}>
                <CardMedia
                  component="img"
                  src={formSignature}
                  css={imageSignatureCss}
                  onClick={() => setIsOpen(true)}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Close
                  fontSize="small"
                  sx={{ color: theme.palette.primary.hint, mr: 1 }}
                />
                <HorizontalLine
                  noMargin
                  customBottomBorderColor={theme.palette.primary.hint}
                />
              </Box>
            </Box>
          </>
        )}

        {!formSignature && (
          <>
            <SignatureTabs
              selectedTab={selectedTab}
              handleClear={() => handleClear}
              setSelectedTab={setSelectedTab}
              addMarginBottom
              allowUseOfDefaultSignature={allowUseOfDefaultSignature}
            />
            {selectedTab === SignatureTabsOptions.UPLOAD && (
              <>
                <Box
                  css={css`
                    padding: ${theme.spacing(5)};
                    border: 1px dashed ${theme.palette.common.lightGray};
                    border-radius: 6px;
                  `}
                >
                  <UploadedFileSection
                    uploadedFile={uploadedFile!}
                    setUploadedFile={(callback) => {
                      const previouslyUploadedFiles = null;
                      const files = callback(previouslyUploadedFiles);
                      const file = files[0];
                      if (file !== undefined) {
                        setUploadedFile(file);

                        // TODO: Consider transcoing this
                        saveSignature(file.base64 as string);
                      }
                    }}
                    setFileError={setFileError}
                    fileError={fileError!}
                  />
                </Box>
                <Box sx={{ mx: 3 }}>
                  <HelperText
                    subtext={t(
                      "components.signatureDialog.uploadedFileHelperText",
                    )}
                  />
                </Box>
              </>
            )}
            {selectedTab === SignatureTabsOptions.DRAW && (
              <Box
                css={css`
                  padding: ${theme.spacing(5)};
                  border: 1px dashed ${theme.palette.common.lightGray};
                  border-radius: 6px;
                `}
              >
                <Box
                  onClick={() => setIsOpen(true)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    css={css`
                      color: ${theme.palette.primary.hint};
                      font-style: italic;
                      &:hover {
                        cursor: grab;
                      }
                    `}
                  >
                    {t("pages.common.signature.drawSignatureLabel")}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Close
                    fontSize="small"
                    sx={{ color: theme.palette.primary.hint, mr: 1 }}
                  />
                  <HorizontalLine
                    noMargin
                    customBottomBorderColor={theme.palette.primary.hint}
                  />
                </Box>
              </Box>
            )}
          </>
        )}
        {(subtext || errorMessage) && (
          <HelperText subtext={subtext} errorMessage={errorMessage} />
        )}
      </FormControl>
    </Element>
  );
};
